import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

const SkeletonRow = () => (
  <div className="flex items-center gap-3 px-4 py-3 animate-pulse">
    <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-2 bg-gray-100 rounded w-1/2" />
    </div>
    <div className="h-5 w-12 bg-gray-200 rounded-full" />
  </div>
);

const DeviceList = ({
  devices,
  selectedDeviceId,
  onDeviceClick,
  activeFilters = [],
  swimZones = [],
  /** When true, show an "LG Bands" fleet row even if no band devices pass filters (e.g. no Firestore docs yet). */
  includeEmptyLGBandsCategory = false,
}) => {
  const isLoading = useSelector(state => state.devices.loading);

  // A single, smarter log to give an overview of the list's state.
  // This will only run in development to avoid cluttering the production console.
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[DeviceList] Rendered with ${devices.length} devices.`, {
        activeFilters,
        selectedDeviceId,
      });
    }
  }, [devices, activeFilters, selectedDeviceId]);

  // State for managing collapsed/expanded categories
  const [collapsedCategories, setCollapsedCategories] = React.useState(new Set());

  // Re-render periodically so "Xm ago" / "Just now" stays current without clicking another row
  const [, setRelativeTimeTick] = React.useState(0);
  useEffect(() => {
    const intervalMs = 60_000;
    const id = setInterval(() => setRelativeTimeTick((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, []);

  // Function to toggle category collapse/expand
  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  // Helper function to get battery color based on level
  const isBatteryUnknown = (level) => level === null || level === undefined || level === 'N/A';

  const getBatteryColor = (level) => {
    if (isBatteryUnknown(level)) return 'text-gray-400';
    if (level >= 80) return 'text-green-600';
    if (level >= 50) return 'text-yellow-600';
    if (level >= 20) return 'text-orange-600';
    return 'text-red-600';
  };

  // Helper function to get battery background color
  const getBatteryBgColor = (level) => {
    if (isBatteryUnknown(level)) return 'bg-gray-50';
    if (level >= 80) return 'bg-green-50';
    if (level >= 50) return 'bg-yellow-50';
    if (level >= 20) return 'bg-orange-50';
    return 'bg-red-50';
  };

  // Helper function to get battery border color
  const getBatteryBorderColor = (level) => {
    if (isBatteryUnknown(level)) return 'border-gray-200';
    if (level >= 80) return 'border-green-200';
    if (level >= 50) return 'border-yellow-200';
    if (level >= 20) return 'border-orange-200';
    return 'border-red-200';
  };

  // Helper function to get battery fill color
  const getBatteryFillColor = (level) => {
    if (isBatteryUnknown(level)) return 'bg-gray-300';
    if (level >= 80) return 'bg-green-500';
    if (level >= 50) return 'bg-yellow-500';
    if (level >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === 'online' || statusLower === 'active' || statusLower === 'running') return 'text-green-600 bg-green-50 border-green-200';
    if (statusLower === 'offline' || statusLower === 'inactive' || statusLower === 'stopped') return 'text-red-600 bg-red-50 border-red-200';
    if (statusLower === 'warning' || statusLower === 'maintenance') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const formatDateStr = (value) => {
    if (!value || typeof value !== 'string') return value;
    const parts = value.split('-');
    if (parts.length !== 3) return value;
    const [year, month, day] = parts;
    if (!year || !month || !day) return value;
    return `${day}-${month}-${year}`;
  };

  // Helper function to count active and inactive devices
  const getStatusCounts = (devices) => {
    let active = 0;
    let inactive = 0;
    
    devices.forEach(device => {
      const status = device.status?.toLowerCase() || '';
      
      // Active statuses
      if (['online', 'active', 'running'].includes(status)) {
        active++;
      }
      // Inactive statuses
      else if (['offline', 'inactive', 'stopped'].includes(status)) {
        inactive++;
      }
      // Count unknown/other statuses as inactive
      else {
        inactive++;
      }
    });
    
    return { active, inactive };
  };

  // Helper function to get device type icon (used for non-mobile layout)
  const getDeviceIcon = (deviceType) => {
    if (!deviceType) return '📡';
    
    const type = String(deviceType).toLowerCase().trim();
    
    // Specific vehicle type mappings
    if (type === 'jeep' || type === 'car') return '🚗';
    if (type === 'truck' || type === 'mini tempo' || type === 'minitempo' || type === 'hiab truck') return '🚚';
    if (type === 'scooter') return '🛴';
    if (type === 'motorcycle') return '🏍️';
    if (type === 'bike' || type === 'ebike') return '🚲';
    
    // Pattern-based matching for other types
    if (type.includes('mobile') || type.includes('phone')) return '📱';
    if (type === 'lg_band' || type.includes('lg_band') || type === 'adafruit') return '⌚';
    if (type.includes('triton')) return '🚤';
    if (type.includes('aurus')) return '🏍️';
    if (type.includes('sea') || type.includes('horse')) return '🐎';
    if (type.includes('vehicle')) return '🚗';
    if (type.includes('boat')) return '⛵';
    
    return '📡';
  };

  

  // Helper function to format last seen time
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Unknown';
    
    // Handle different date formats
    let date;
    if (typeof lastSeen === 'string') {
      date = new Date(lastSeen);
    } else if (lastSeen.toDate && typeof lastSeen.toDate === 'function') {
      // Firebase Timestamp
      date = lastSeen.toDate();
    } else if (lastSeen.seconds) {
      // Firebase Timestamp object
      date = new Date(lastSeen.seconds * 1000);
    } else {
      date = new Date(lastSeen);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date for lastSeen:', lastSeen);
      return 'Unknown';
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Helper function to get device type for grouping
  const getDeviceTypeForGrouping = (device) => {
    const deviceType = device.Vehicletype || device.vehicleType || device.device_type;
    if (!deviceType) return 'Unknown';
    
    // Normalize device types for grouping
    const normalizedType = String(deviceType).toLowerCase();
    if (normalizedType === 'lg_band' || normalizedType === 'adafruit' || normalizedType.includes('lg_band')) {
      return 'LG Bands';
    }
    if (normalizedType.includes('atv')) return 'ATV';
    if (normalizedType.includes('ebike')) return 'Ebike';
    if (normalizedType.includes('mobile')) return 'Mobile';
    if (normalizedType.includes('triton')) return 'Triton';
    if (normalizedType.includes('aurus')) return 'Aurus';
    if (normalizedType.includes('sea') || normalizedType.includes('horse')) return 'Sea Horse';
    if (normalizedType.includes('boat') || normalizedType.includes('sail')) return 'Boat';
    if (normalizedType.includes('vehicle') || normalizedType.includes('car') || normalizedType.includes('jeep')) return 'Vehicle';
    if (normalizedType.includes('bike')) return 'Bike';
    if (normalizedType.includes('truck')) return 'Truck';
    if (normalizedType.includes('tower')) return 'Towers';
    
    return deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
  };

  const hasSwimZones = Array.isArray(swimZones) && swimZones.length > 0;
  const swimZoneCount = hasSwimZones ? swimZones.length : 0;
  const SWIMZONE_CATEGORY_KEY = 'Swimzones';

  // Check if we're showing "Total Fleet" (all devices) or multiple filters
  const isShowingTotalFleet = activeFilters.includes('all') || activeFilters.length === 0;
  const isShowingMultipleFilters = !isShowingTotalFleet && activeFilters.length > 1;

  // Group devices by type when showing Total Fleet or multiple filters
  const groupedDevices = React.useMemo(() => {
    if (!isShowingTotalFleet && !isShowingMultipleFilters) {
      return { 'Filtered Results': devices };
    }

    const groups = {};
    devices.forEach(device => {
      const groupType = getDeviceTypeForGrouping(device);
      if (!groups[groupType]) {
        groups[groupType] = [];
      }
      groups[groupType].push(device);
    });

    if (includeEmptyLGBandsCategory && !groups['LG Bands']) {
      groups['LG Bands'] = [];
    }

    // Sort groups by name and devices within each group by name
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(groupName => {
      sortedGroups[groupName] = groups[groupName].sort((a, b) => {
        const nameA = a.displayName || a.name || a.device_id || '';
        const nameB = b.displayName || b.name || b.device_id || '';
        return nameA.localeCompare(nameB);
      });
    });

    return sortedGroups;
  }, [devices, isShowingTotalFleet, isShowingMultipleFilters, includeEmptyLGBandsCategory]);

  // Determine if all categories are currently collapsed (including swimzones)
  const allCategoryKeys = React.useMemo(() => {
    const keys = Object.keys(groupedDevices || {});
    if (hasSwimZones) {
      keys.push(SWIMZONE_CATEGORY_KEY);
    }
    return keys;
  }, [groupedDevices, hasSwimZones]);
  
  const isAllCollapsed = React.useMemo(
    () => allCategoryKeys.length > 0 && allCategoryKeys.every(cat => collapsedCategories.has(cat)),
    [allCategoryKeys, collapsedCategories]
  );

  // Loading skeleton
  if (isLoading && (!devices || devices.length === 0)) {
    return (
      <div className="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-h-[calc(100vh-240px)]">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-gray-200">
          <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
        </div>
        <div className="divide-y divide-gray-100">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}
        </div>
      </div>
    );
  }

  // Empty state
  if (!devices || devices.length === 0) {
    return (
      <div className="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700">Fleet Devices</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="text-4xl mb-3">📡</div>
          <p className="text-gray-500 text-sm font-medium">No devices match your filters</p>
          <p className="text-gray-400 text-xs mt-1">Try adjusting your search or filter criteria</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-h-[calc(100vh-240px)]">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700 min-w-0">
            Fleet Devices
          </h3>
          {((isShowingTotalFleet || isShowingMultipleFilters) && Object.keys(groupedDevices).length > 1) || hasSwimZones ? (
            <span
              onClick={() => {
                const allCategories = Object.keys(groupedDevices);
                if (hasSwimZones) {
                  allCategories.push(SWIMZONE_CATEGORY_KEY);
                }
                const allCollapsed = allCategories.every(cat => collapsedCategories.has(cat));
                if (allCollapsed) {
                  setCollapsedCategories(new Set());
                } else {
                  setCollapsedCategories(new Set(allCategories));
                }
              }}
              className="text-[11px] lg:text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer font-medium transition-colors whitespace-nowrap shrink-0"
              title={isAllCollapsed ? 'Expand all categories' : 'Collapse all categories'}
            >
              <span className="lg:hidden">{!isAllCollapsed ? 'Collapse' : 'Expand'}</span>
              <span className="hidden lg:inline">{isAllCollapsed ? 'Expand All' : 'Collapse All'}</span>
            </span>
          ) : null}
        </div>
        <div className="mt-1 text-[11px] lg:text-xs text-gray-500">
          ({devices.length} device{devices.length !== 1 ? 's' : ''}{hasSwimZones ? `, ${swimZoneCount} swimzone${swimZoneCount !== 1 ? 's' : ''}` : ''})
        </div>
      </div>

      {/* Device List */}
      <div className="flex-1 min-h-[120px] overflow-y-auto" role="listbox" aria-label="Device list">
        {devices.length === 0 && !hasSwimZones ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm text-center">No devices found</p>
            <p className="text-gray-400 text-xs text-center mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {devices.length > 0 && Object.entries(groupedDevices).map(([groupName, groupDevices]) => (
              <div key={groupName}>
                {/* Group Header */}
                {isShowingTotalFleet && (
                  <div 
                    className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200 z-10 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => toggleCategory(groupName)}
                    title={`Click to ${collapsedCategories.has(groupName) ? 'expand' : 'collapse'} ${groupName} category`}
                  >
                    {(() => {
                      const { active, inactive } = getStatusCounts(groupDevices);
                      return (
                      <>
                      {/* Mobile header variant */}
                      <div className="flex items-center lg:hidden relative">
                        <div className="flex-1 flex items-center pr-2">
                          <h4 className="text-[13px] font-semibold text-gray-700 capitalize select-none">
                            {groupName}
                            <span className="ml-1 font-normal text-gray-500">({groupDevices.length})</span>
                          </h4>
                        </div>
                        {/* Vertical partition at halfway point */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
                        <div className="flex-1 flex items-center gap-3 pl-4">
                          {/* Active/Inactive counts aligned consistently from partition */}
                          <div className="flex flex-col text-[11px] leading-tight">
                            <span className="text-green-600 font-medium">Active:</span>
                            <span className="text-red-600 font-medium">Inactive:</span>
                          </div>
                          <div className="flex flex-col text-[11px] leading-tight">
                            <span className="text-green-600 font-medium">{active}</span>
                            <span className="text-red-600 font-medium">{inactive}</span>
                          </div>
                          {/* Mobile: plain chevron on the right */}
                          <svg
                            className={`w-4 h-4 text-gray-600 transition-transform duration-200 flex-shrink-0 ml-auto ${
                              collapsedCategories.has(groupName) ? 'rotate-0' : 'rotate-90'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Desktop header variant */}
                      <div className="hidden lg:flex items-center relative">
                        <div className="flex-1 flex items-center space-x-2 pr-2">
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 transition-colors duration-200">
                            <svg 
                              className={`w-3 h-3 text-gray-600 transition-transform duration-200 ${
                                collapsedCategories.has(groupName) ? 'rotate-0' : 'rotate-90'
                              }`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M9 5l7 7-7 7" 
                              />
                            </svg>
                          </div>
                          <h4 className="text-sm font-semibold text-gray-700 capitalize select-none">
                            {groupName}
                          </h4>
                        </div>
                        {/* Vertical partition at halfway point */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300 transform -translate-x-1/2"></div>
                        <div className="flex-1 flex items-center gap-3 pl-16">
                          {/* Active/Inactive counts aligned consistently from partition */}
                          <div className="flex flex-col text-xs leading-tight">
                            <span className="text-green-600 font-medium">Active:</span>
                            <span className="text-red-600 font-medium">Inactive:</span>
                          </div>
                          <div className="flex flex-col text-xs leading-tight">
                            <span className="text-green-600 font-medium">{active}</span>
                            <span className="text-red-600 font-medium">{inactive}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full select-none ml-auto">
                            {groupDevices.length} device{groupDevices.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </>
                  );
                  })()}
                  </div>
                )}
                
                {/* Group Devices - Collapsible */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isShowingTotalFleet && collapsedCategories.has(groupName)
                    ? 'max-h-0 opacity-0'
                    : 'max-h-[16000px] opacity-100'
                }`}>
                  {groupDevices.map((device, index) => {
                  const deviceKey = `device-list-${device.device_id || 'unknown'}-${device.name || 'unnamed'}-${index}`;
                  const isSelected = selectedDeviceId === device.device_id;
                  const batteryLevel = device.battery_level;
                  const status = device.status;
                  // Use Vehicletype first (most specific), then vehicleType, then device_type
                  const deviceType = device.Vehicletype || device.vehicleType || device.device_type;

                  return (
                    <div
                      key={deviceKey}
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (onDeviceClick) onDeviceClick(device);
                        }
                      }}
                      className={`group relative px-3 py-2.5 cursor-pointer transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-inset ${
                        isSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onDeviceClick) onDeviceClick(device);
                      }}
                    >
                      <div className="flex items-center space-x-2.5">
                        {/* Desktop-only Device Icon */}
                        <div className="hidden lg:flex flex-shrink-0">
                          <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-lg flex items-center justify-center text-sm overflow-hidden">
                            {(() => {
                              const normalizedType = String(deviceType || '').toLowerCase().trim();
                              if (normalizedType.includes('sea') || normalizedType.includes('horse')) {
                                return (
                                  <img 
                                    src="/seahorse_icon.png" 
                                    alt="Sea Horse" 
                                    className="w-full h-full object-contain p-0.5"
                                  />
                                );
                              }
                              return getDeviceIcon(deviceType);
                            })()}
                          </div>
                        </div>
                        {/* Device Info - unified layout (no dual mobile/desktop blocks) */}
                        <div className="flex-1 min-w-0">
                          {/* Device Name */}
                          <h4 className="text-sm font-semibold text-gray-900 mb-1 truncate">
                            {device.displayName || device.name || device.device_id}
                          </h4>

                          {/* Device Type, Last Seen, Status and Battery */}
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="text-xs text-gray-600 capitalize truncate">
                                {deviceType || 'Unknown Type'}
                              </span>
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                {formatLastSeen(device.last_seen || device.lastSeen || device.last_seen_time || device.lastSeenTime || device.timestamp)}
                              </span>
                            </div>

                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {/* Status Indicator */}
                              <div className={`px-1.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
                                {status || 'Unknown'}
                              </div>

                              {/* Compact Battery Indicator */}
                              <div className={`flex items-center space-x-1 px-1.5 py-0.5 rounded-md border ${getBatteryBgColor(batteryLevel)} ${getBatteryBorderColor(batteryLevel)}`}>
                                <div className="relative flex items-center">
                                  <div className="relative w-4 h-2.5 bg-white border border-gray-300 rounded-sm overflow-hidden">
                                    {batteryLevel && batteryLevel !== 'N/A' ? (
                                      <div
                                        className={`absolute top-0 left-0 h-full ${getBatteryFillColor(batteryLevel)} transition-all duration-300`}
                                        style={{ width: `${Math.min(Math.max(batteryLevel, 0), 100)}%` }}
                                      />
                                    ) : (
                                      <div className="absolute top-0 left-0 h-full w-full bg-gray-200" />
                                    )}
                                  </div>
                                  <div className="w-0.5 h-1.5 bg-gray-300 rounded-r-sm ml-0.5" />
                                </div>

                                <span className={`text-xs font-semibold ${getBatteryColor(batteryLevel)}`}>
                                  {batteryLevel && batteryLevel !== 'N/A' ? `${batteryLevel}%` : 'N/A'}
                                </span>

                                <div className="flex-shrink-0">
                                  {batteryLevel && batteryLevel !== 'N/A' ? (
                                    batteryLevel >= 80 ? (
                                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                    ) : batteryLevel >= 50 ? (
                                      <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
                                    ) : batteryLevel >= 20 ? (
                                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                                    ) : (
                                      <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                                    )
                                  ) : (
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      {/* Selection Indicator */}
                      {isSelected && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                        </div>
                      )}

                      {/* Hover Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-200 pointer-events-none"></div>
                    </div>
                  </div>
                  );
                  })}
                </div>
              </div>
            ))}
            {hasSwimZones && (
              <div>
                {/* Swimzone Header - Matching device category style */}
                <div 
                    className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200 z-10 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                    onClick={() => toggleCategory(SWIMZONE_CATEGORY_KEY)}
                    title={`Click to ${collapsedCategories.has(SWIMZONE_CATEGORY_KEY) ? 'expand' : 'collapse'} Swimzones category`}
                  >
                    <div>
                      {/* Mobile header variant */}
                      <div className="flex items-center lg:hidden relative">
                        <div className="flex-1 flex items-center pr-2">
                          <h4 className="text-[13px] font-semibold text-gray-700 capitalize select-none">
                            Swimzones
                            <span className="ml-1 font-normal text-gray-500">({swimZoneCount})</span>
                          </h4>
                        </div>
                        {/* Mobile: plain chevron on the right */}
                        <div className="flex-1 flex items-center justify-end">
                          <svg
                            className={`w-4 h-4 text-gray-600 transition-transform duration-200 flex-shrink-0 ml-auto ${
                              collapsedCategories.has(SWIMZONE_CATEGORY_KEY) ? 'rotate-0' : 'rotate-90'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Desktop header variant */}
                      <div className="hidden lg:flex items-center relative">
                        <div className="flex-1 flex items-center space-x-2 pr-2">
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 transition-colors duration-200">
                            <svg 
                              className={`w-3 h-3 text-gray-600 transition-transform duration-200 ${
                                collapsedCategories.has(SWIMZONE_CATEGORY_KEY) ? 'rotate-0' : 'rotate-90'
                              }`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M9 5l7 7-7 7" 
                              />
                            </svg>
                          </div>
                          <h4 className="text-sm font-semibold text-gray-700 capitalize select-none">
                            Swimzones
                          </h4>
                        </div>
                        <div className="flex-1 flex items-center justify-end">
                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full select-none">
                            {swimZoneCount} swimzone{swimZoneCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                
                {/* Swimzone Items - Collapsible */}
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  collapsedCategories.has(SWIMZONE_CATEGORY_KEY)
                    ? 'max-h-0 opacity-0'
                    : 'max-h-[16000px] opacity-100'
                }`}>
                  <div className="divide-y divide-gray-100">
                    {swimZones.map((zone, index) => (
                      <div
                        key={`swimzone-${zone.id || index}`}
                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                        onClick={() => {
                          if (!onDeviceClick) return;
                          let lat = zone.lat || zone.latitude;
                          let lng = zone.lng || zone.longitude;
                          if (!lat || !lng) {
                            const geoField = zone.co_ordinates || zone.coordinates;
                            if (geoField) {
                              if (geoField.latitude !== undefined) { lat = geoField.latitude; lng = geoField.longitude; }
                              else if (Array.isArray(geoField) && geoField.length >= 2) { lat = geoField[0]; lng = geoField[1]; }
                            }
                          }
                          if (typeof lat === 'string') lat = parseFloat(lat);
                          if (typeof lng === 'string') lng = parseFloat(lng);
                          if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                            onDeviceClick({ device_id: `swimzone-${zone.id}`, displayName: zone.beach_name || zone.point_name || 'Swimzone', last_latitude: lat, last_longitude: lng, device_type: 'swimzone', status: 'active' });
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {zone.beach_name || zone.point_name || 'Swimzone'}
                            </p>
                            {zone.beach_name && zone.point_name && (
                              <p className="text-xs text-gray-500 truncate">{zone.point_name}</p>
                            )}
                          </div>
                          {zone.date_str && (
                            <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateStr(zone.date_str)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      {devices.length > 0 && (
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
          <div className="hidden lg:flex flex-col items-center justify-between text-xs text-gray-500">
            <span>Click on any device to view</span>
            {/* <span>({devices.length} total Devices)</span> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceList; 